/**
 * PostMessage Communication Types
 * Defines secure messaging protocol between widget and parent page
 */

/**
 * Message types for widget communication
 */
export enum MessageType {
  // Handshake (secure connection establishment)
  HANDSHAKE_REQUEST = 'widget:handshake:request',
  HANDSHAKE_RESPONSE = 'widget:handshake:response',
  HANDSHAKE_COMPLETE = 'widget:handshake:complete',
  
  // Initialization
  WIDGET_READY = 'widget:ready',
  WIDGET_INIT = 'widget:init',

  // Resize events
  RESIZE_REQUEST = 'widget:resize:request',
  RESIZE_RESPONSE = 'widget:resize:response',

  // Navigation events
  NAVIGATE = 'widget:navigate',
  NAVIGATE_BACK = 'widget:navigate:back',

  // Booking events
  BOOKING_STARTED = 'widget:booking:started',
  BOOKING_COMPLETED = 'widget:booking:completed',
  BOOKING_CANCELLED = 'widget:booking:cancelled',
  BOOKING_ERROR = 'widget:booking:error',

  // Error events
  ERROR = 'widget:error',

  // Analytics events
  ANALYTICS_EVENT = 'widget:analytics:event',

  // Session events
  SESSION_START = 'widget:session:start',
  SESSION_END = 'widget:session:end',
}

/**
 * Base message structure
 */
export interface BaseMessage {
  /** Message type */
  type: MessageType;

  /** Message timestamp */
  timestamp: string;

  /** Unique message ID */
  messageId: string;

  /** Origin verification */
  origin: string;
}

/**
 * Handshake request message (from parent to widget)
 */
export interface HandshakeRequestMessage extends BaseMessage {
  type: MessageType.HANDSHAKE_REQUEST;
  data: {
    /** Unique handshake token */
    token: string;
    /** Parent origin for verification */
    parentOrigin: string;
  };
}

/**
 * Handshake response message (from widget to parent)
 */
export interface HandshakeResponseMessage extends BaseMessage {
  type: MessageType.HANDSHAKE_RESPONSE;
  data: {
    /** Echo back the token */
    token: string;
    /** Widget origin for verification */
    widgetOrigin: string;
  };
}

/**
 * Handshake complete message (from parent to widget)
 */
export interface HandshakeCompleteMessage extends BaseMessage {
  type: MessageType.HANDSHAKE_COMPLETE;
  data: {
    /** Confirmation token */
    token: string;
  };
}

/**
 * Widget initialization message
 */
export interface InitMessage extends BaseMessage {
  type: MessageType.WIDGET_INIT;
  data: {
    config: Record<string, unknown>;
  };
}

/**
 * Widget ready message
 */
export interface ReadyMessage extends BaseMessage {
  type: MessageType.WIDGET_READY;
  data: {
    version: string;
    initialHeight: number;
  };
}

/**
 * Resize request message
 */
export interface ResizeRequestMessage extends BaseMessage {
  type: MessageType.RESIZE_REQUEST;
  data: {
    height: number;
    width?: number;
  };
}

/**
 * Resize response message
 */
export interface ResizeResponseMessage extends BaseMessage {
  type: MessageType.RESIZE_RESPONSE;
  data: {
    success: boolean;
    height: number;
    width?: number;
  };
}

/**
 * Navigation message
 */
export interface NavigateMessage extends BaseMessage {
  type: MessageType.NAVIGATE;
  data: {
    path: string;
    state?: Record<string, unknown>;
  };
}

/**
 * Booking completed message
 */
export interface BookingCompletedMessage extends BaseMessage {
  type: MessageType.BOOKING_COMPLETED;
  data: {
    confirmationId: string;
    appointmentId: string;
    appointmentTime: string;
    providerName: string;
  };
}

/**
 * Error message
 */
export interface ErrorMessage extends BaseMessage {
  type: MessageType.ERROR;
  data: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

/**
 * Analytics event message
 */
export interface AnalyticsEventMessage extends BaseMessage {
  type: MessageType.ANALYTICS_EVENT;
  data: {
    eventName: string;
    properties?: Record<string, unknown>;
  };
}

/**
 * Union type of all message types
 */
export type WidgetMessage =
  | HandshakeRequestMessage
  | HandshakeResponseMessage
  | HandshakeCompleteMessage
  | InitMessage
  | ReadyMessage
  | ResizeRequestMessage
  | ResizeResponseMessage
  | NavigateMessage
  | BookingCompletedMessage
  | ErrorMessage
  | AnalyticsEventMessage;

/**
 * Message handler function type
 */
export type MessageHandler<T extends BaseMessage = BaseMessage> = (
  message: T
) => void | Promise<void>;

/**
 * Message validation result
 */
export interface MessageValidationResult {
  /** Validation success */
  valid: boolean;

  /** Validation error message */
  error?: string;

  /** Validated message */
  message?: WidgetMessage;
}
